import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";

// Constants
const RENDER_PIPELINE_ALPHA = 0.001; // Keep in pipeline without visible render
const VISIBLE_ALPHA = 1;

/**
 * HoldAndWinCounterController - Manages the counter spine animation
 * Shows remaining respins in Hold and Win feature (3, 2, 1, 0)
 * Plays transition animations when counter value changes
 */
class HoldAndWinCounterController {
  constructor(container, dimensions, symbolHeight, device, orientation) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolHeight = symbolHeight;
    this.device = device;
    this.orientation = orientation;

    // Spine instance
    this.counterSpine = null;
    this.initialized = false;
    this.isCleanedUp = false;

    // Internal caches for performance optimization
    this._availableAnimationsCache = null; // Cache for getAvailableAnimations result
    this._hasAnimationCache = new Map(); // Cache for hasAnimation results
    this._scaleCache = null; // Cache for calculated scale value
    this._lastSymbolHeight = null; // Track symbol height changes for cache invalidation
    this._lastDevice = null; // Track device changes for cache invalidation
    this._lastOrientation = null; // Track orientation changes for cache invalidation

    this.initializeCounterSpine();
  }

  /**
   * Check if controller is ready for operations
   */
  isReady() {
    return this.counterSpine?.state && this.initialized && !this.isCleanedUp;
  }

  /**
   * Apply proportional scaling and centering to spine animation
   */
  applyScaleAndCenter() {
    if (!this.counterSpine || !this.dimensions) return;

    // Check cache
    if (this._scaleCache !== null && 
        this._lastSymbolHeight === this.symbolHeight && 
        this._lastDevice === this.device && 
        this._lastOrientation === this.orientation) {
      // Use cached scale value
      this.counterSpine.scale.set(this._scaleCache);
      this.counterSpine.x = 0;
      this.counterSpine.y = 0;
      return;
    }

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply device/orientation-specific multipliers
    const deviceMultiplier = this.getDeviceScaleMultiplier();
    scale *= deviceMultiplier;

    this.counterSpine.scale.set(scale);

    // Center the counter (parent container handles main positioning)
    this.counterSpine.x = 0;
    this.counterSpine.y = 0;

    // Update cache
    this._scaleCache = scale;
    this._lastSymbolHeight = this.symbolHeight;
    this._lastDevice = this.device;
    this._lastOrientation = this.orientation;
  }

  /**
   * Get device-specific scale multiplier for spine animations
   * Currently returns 1.0 for all devices (can be adjusted if needed)
   */
  getDeviceScaleMultiplier() {
    // Cache device multiplier (currently always 1.0, but cache for future changes)
    if (this._deviceMultiplierCache === undefined) {
      this._deviceMultiplierCache = 1.0;
    }
    return this._deviceMultiplierCache;
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   */
  updateDimensions(dimensions, symbolHeight, device, orientation) {
    const oldDimensions = this.dimensions;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== dimensions?.width ||
      oldDimensions.height !== dimensions?.height ||
      this.device !== device ||
      this.orientation !== orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;

    if (!dimensionsChanged && !symbolHeightChanged) {
      return;
    }

    this.dimensions = dimensions;
    this.symbolHeight = symbolHeight;
    this.device = device;
    this.orientation = orientation;

    // Invalidate caches when dimensions change
    if (dimensionsChanged || symbolHeightChanged) {
      this._scaleCache = null;
      this._lastSymbolHeight = null;
      this._lastDevice = null;
      this._lastOrientation = null;
    }

    if (
      this.counterSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }
  }

  /**
   * Initialize the counter spine
   */
  initializeCounterSpine() {
    try {
      this.counterSpine = this.createCounterSpine();

      if (!this.counterSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.counterSpine.skeleton) missingComponents.push("skeleton");
      if (!this.counterSpine.state) missingComponents.push("state");
      if (!this.counterSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.counterSpine);
      this.counterSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.counterSpine.parent !== this.container) {
        return;
      }

      // Initialize spine structure (populates skeleton hierarchy and warms up)
      this.initializeSpineStructure();

      // Invalidate animation caches when spine is initialized (spine structure may have changed)
      this._availableAnimationsCache = null;
      this._hasAnimationCache.clear();

      this.initialized = true;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   * Minimal requirement: set one animation + update once
   */
  initializeSpineStructure() {
    if (!this.counterSpine || !this.counterSpine.state) {
      return;
    }

    try {
      // Set any animation to force spine to load bones/slots/attachments
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        // Try to use counter_3 first (most common initial state), fallback to first available
        const counter3Animation = animations.find(
          (anim) => anim === "counter_3"
        );
        const animationToUse = counter3Animation || animations[0];

        this.counterSpine.state.setAnimation(0, animationToUse, false);
        this.counterSpine.state.update(0.016); // Update one frame

        // Only call updateWorldTransform if physics is available (some spines don't have physics)
        try {
          if (this.counterSpine.skeleton.physics !== undefined) {
            this.counterSpine.skeleton.updateWorldTransform(); // Populate hierarchy
          }
        } catch (transformError) {
          // Skip updateWorldTransform if physics is not initialized - not critical for warmup
        }

        this.counterSpine.state.clearTracks(); // Clear after warmup
      }

      // Keep spine ready but hidden initially (will be shown when Hold and Win becomes active)
      // Using 0.001 instead of 0 keeps it in render pipeline for better performance
      this.counterSpine.alpha = RENDER_PIPELINE_ALPHA;
    } catch (error) {
      // Silent fail - warmup is optional
    }
  }

  /**
   * Create counter spine from loaded assets
   */
  createCounterSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "hns_counter-skeleton";
      const atlasAlias = "hns_counter-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const counterSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!counterSpine || !counterSpine.skeleton || !counterSpine.state) {
        return null;
      }

      if (
        !counterSpine.skeleton.data ||
        !counterSpine.skeleton.data.animations
      ) {
        return null;
      }

      return counterSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animations from the spine
   */
  getAvailableAnimations() {
    // Return cached result if available
    if (this._availableAnimationsCache) {
      return this._availableAnimationsCache;
    }

    const animations = (
      this.counterSpine?.skeleton?.data?.animations.map((a) => a.name) || []
    );
    
    // Cache result
    this._availableAnimationsCache = animations;
    
    return animations;
  }

  /**
   * Check if animation exists
   */
  hasAnimation(animationName) {
    // Check cache
    if (this._hasAnimationCache.has(animationName)) {
      return this._hasAnimationCache.get(animationName);
    }

    const hasAnim = this.counterSpine?.skeleton?.data?.animations.some(
      (anim) => anim.name === animationName
    );
    
    // Cache result
    this._hasAnimationCache.set(animationName, hasAnim);
    
    // Limit cache size
    if (this._hasAnimationCache.size > 20) {
      const firstKey = this._hasAnimationCache.keys().next().value;
      this._hasAnimationCache.delete(firstKey);
    }
    
    return hasAnim;
  }

  /**
   * Show counter with initial state (counter_3)
   * Called when Hold and Win becomes active to display the counter immediately
   */
  showCounter() {
    if (!this.isReady()) {
      return;
    }

    // Show counter_3 static state
    this.setStaticState(3);
    // Update to ensure it renders immediately
    if (this.counterSpine.state) {
      this.counterSpine.state.update(0.016);
    }
  }

  /**
   * Update counter display - main entry point for state changes
   * @param {number} newRespins - New respins value (0-3)
   * @param {number|null} prevRespins - Previous respins value
   * @param {boolean} playSound - Whether to play sounds (default: true)
   */
  updateCounter(newRespins, prevRespins, playSound = true) {
    if (!this.isReady()) {
      return;
    }

    // Make sure spine is visible
    this.counterSpine.alpha = VISIBLE_ALPHA;

    // If first time or no previous value, show static state
    // Only use static state for initial value if it's 0 or 3 (the only static states)
    // For 1 or 2, play transition animations to show the correct value
    if (prevRespins === null || prevRespins === undefined) {
      if (newRespins === 3 || newRespins === 0) {
        this.setStaticState(newRespins, !playSound); // silent = !playSound
      } else if (newRespins === 2) {
        // Show transition from 3 to 2 to display correct initial value
        this.playTransition(3, 2, playSound);
      } else if (newRespins === 1) {
        // Show transition from 2 to 1 to display correct initial value (no 3_to_1 animation exists)
        this.playTransition(2, 1, playSound);
      }
      return;
    }

    // If same value, do nothing
    if (newRespins === prevRespins) {
      return;
    }

    // Play transition animation
    this.playTransition(prevRespins, newRespins, playSound);
  }

  /**
   * Play transition animation from one value to another
   *
   * Flow:
   * - Decreasing (3→2, 2→1, 1→0): Play transition, stay on last frame
   * - Resetting to 3 (0→3, 1→3, 2→3): Play transition, then transition to counter_3 static
   * @param {number} from - Starting value
   * @param {number} to - Ending value
   * @param {boolean} playSound - Whether to play sounds (default: true)
   */
  playTransition(from, to, playSound = true) {
    if (!this.isReady()) return;

    let animationName = `counter_${from}_to_${to}`;

    // Handle transitions to 3: use counter_0_to_3 as fallback for missing animations or reset
    if (to === 3 && (from === 3 || from === 1)) {
      animationName = "counter_0_to_3";
    }

    // Check if animation exists
    if (this.hasAnimation(animationName)) {
      try {
        // Make spine visible
        this.counterSpine.alpha = VISIBLE_ALPHA;

        // Clear any existing animations
        this.counterSpine.state.clearTracks();
        this.counterSpine.state.clearListeners();

        // Play transition animation (non-looping)
        this.counterSpine.state.setAnimation(0, animationName, false);

        // Play add_respin sound whenever any animation plays
        // Only play if sound is enabled (skip during intro popup or reload dialog)
        if (playSound) {
          soundManager.play(SOUND_NAME.add_respin);
        }

        // If transitioning TO 3, add listener to transition to counter_3 static after completion
        if (to === 3) {
          this.counterSpine.state.addListener({
            complete: (entry) => {
              if (entry.animation.name === animationName) {
                // Transition to counter_3 static state (silent - no sound)
                this.setStaticState(3, true);
              }
            },
          });
        }
        // For decreasing transitions (3→2, 2→1, 1→0), just let them stay on last frame
        // No listener needed - Spine will naturally stay on last frame
      } catch (error) {
        // Fallback: snap to static state (only if static exists)
        if (to === 3 || to === 0) {
          this.setStaticState(to);
        }
      }
    } else {
      // Fallback: snap to static state (only if static exists)
      if (to === 3 || to === 0) {
        this.setStaticState(to);
      }
    }
  }

  /**
   * Set static state (no transition animation)
   * Only works for values 0 and 3 (the only static states that exist)
   * Immediately shows the spine (alpha = 1)
   * @param {number} value - The counter value (0 or 3)
   * @param {boolean} silent - If true, don't play sound (used when transitioning from animation)
   */
  setStaticState(value, silent = false) {
    if (!this.isReady()) {
      return;
    }

    // Only static states that exist are counter_0 and counter_3
    if (value !== 0 && value !== 3) {
      return;
    }

    const animationName = `counter_${value}`;

    if (this.hasAnimation(animationName)) {
      try {
        // Make spine visible immediately (no fade)
        this.counterSpine.alpha = VISIBLE_ALPHA;

        // Clear any existing animations
        this.counterSpine.state.clearTracks();
        this.counterSpine.state.clearListeners();

        // Set static animation (non-looping, stays on last frame)
        this.counterSpine.state.setAnimation(0, animationName, false);

        // Play add_respin sound only if not silent
        if (!silent) {
          soundManager.play(SOUND_NAME.add_respin);
        }
      } catch (error) {
        // Silent fail
      }
    }
  }

  /**
   * Hide counter by setting alpha to 0 (preserves animation state)
   * Does NOT reset animation - just makes it invisible
   */
  hide() {
    if (!this.counterSpine) {
      return;
    }
    this.counterSpine.alpha = RENDER_PIPELINE_ALPHA;
  }

  /**
   * Show counter by setting alpha to 1 (preserves animation state)
   * Does NOT reset animation - just makes it visible
   */
  show() {
    if (!this.counterSpine) {
      return;
    }
    this.counterSpine.alpha = VISIBLE_ALPHA;
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.isCleanedUp) {
      return;
    }
    this.isCleanedUp = true;

    if (this.counterSpine) {
      this.counterSpine.state?.clearListeners();
      this.counterSpine.state?.clearTracks();
      if (this.counterSpine.state) this.counterSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.counterSpine)) {
        this.container.removeChild(this.counterSpine);
      }
    this.counterSpine.destroy();
    this.counterSpine = null;
    }

    // Clear caches
    this._availableAnimationsCache = null;
    this._hasAnimationCache.clear();
    this._scaleCache = null;
    this._lastSymbolHeight = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initialized = false;
  }
}

export default HoldAndWinCounterController;
